// April 2026 blog posts — written from Patrik Germann's perspective

export const aprilBlogPosts = {
  'claude-opus-4-6-mein-coding-partner': {
    id: 22,
    slug: 'claude-opus-4-6-mein-coding-partner',
    title: {
      de: 'Claude Opus 4.6: Mein täglich genutzter Coding-Partner – und warum er alle anderen schlägt',
      en: 'Claude Opus 4.6: My Daily Coding Partner — And Why It Beats Everything Else'
    },
    excerpt: {
      de: 'Seit dem Release am 5. Februar 2026 programmiere ich täglich mit Claude Opus 4.6. Was die Benchmarks versprechen, hält das Modell im Alltag – und geht noch darüber hinaus. Ein persönlicher Erfahrungsbericht.',
      en: 'Since its release on February 5, 2026, I\'ve been coding with Claude Opus 4.6 every single day. What the benchmarks promise, the model delivers in daily use — and then some. A personal experience report.'
    },
    content: {
      de: `13. April 2026 – 10 Min. Lesezeit

Claude Opus 4.6 ist seit dem 5. Februar 2026 live – und ich nutze es seitdem täglich. Nicht weil ich mir vom Hype mitreissen lasse, sondern weil es schlicht das Beste ist, was ich bisher für komplexe Software-Entwicklung in die Hände bekommen habe.

In diesem Beitrag zeige ich, warum Opus 4.6 für mich nicht nur ein Upgrade war, sondern eine neue Kategorie – und wie es sich im echten Arbeitsalltag gegen GPT-5.4 und Gemini 2.5 Pro schlägt.

⸻

## Die Zahlen, bevor wir über Erfahrung reden

Ich starte ungern mit Benchmarks, weil sie oft wenig mit dem echten Arbeiten zu tun haben. Aber bei Opus 4.6 sind die Zahlen tatsächlich aussagekräftig:

• **Terminal-Bench 2.0** (agentic coding): 65.4% – die höchste Bewertung über alle Frontier-Modelle
• **ARC-AGI-2**: 68.8% – gegenüber 37.6% für Opus 4.5 und 54.2% für GPT-5.2. Der grösste Sprung in einer einzigen Modell-Generation
• **SWE-bench verified**: 80.8% – reale Software-Engineering-Aufgaben, gelöst
• **OSWorld**: 72.7% – autonome Computer-Nutzung, best in class
• **GDPval-AA**: rund 144 Elo-Punkte vor GPT-5.2 bei wirtschaftlich relevanten Wissensaufgaben

Was mich besonders an diesen Zahlen beeindruckt: Sie spiegeln das wider, was ich täglich erlebe. Das ist selten.

⸻

## Was der 1-Millionen-Token-Kontext wirklich bedeutet

1 Million Token Kontext klingt abstrakt. In der Praxis bedeutet es:

Ich kann mein gesamtes Projekt in einen einzigen Kontext laden. Nicht nur einzelne Dateien, nicht nur Module – das ganze Ding. Claude sieht dann alle Abhängigkeiten, alle Interfaces, alle bestehenden Muster gleichzeitig.

Das verändert, wie ich arbeite:
• Refactoring über 20+ Dateien in einer einzigen Session – ohne Kontextverlust
• Bugs, die sich über mehrere Layer ziehen, werden auf einmal erkannt
• Architekturentscheidungen trifft Claude mit Kenntnis des gesamten Codes

Vorher hatte ich immer das Gefühl, dem Modell Puzzleteile hinzuwerfen. Jetzt sieht es das vollständige Bild.

⸻

## Agentic Sessions: Wenn Claude einfach loslegt

Was mich an Opus 4.6 am meisten überrascht hat: Die Fähigkeit, wirklich autonom zu arbeiten und dabei nicht den Faden zu verlieren.

Mit früheren Modellen hatte ich regelmässig das Problem, dass lange Sessions irgendwann driften – das Modell vergisst Entscheidungen, wiederholt sich, oder fängt an, Kompromisse einzugehen, die ich nicht haben wollte.

Opus 4.6 hält die Linie. In mehrstündigen Coding-Sessions bleibt es konsistent:
- Gleicher Code-Stil, auch nach 50+ Iterationen
- Erinnerung an Architektur-Entscheidungen vom Anfang der Session
- Aktives Ansprechen von Inkonsistenzen, die ich mir selbst eingebaut habe

Das ist der Unterschied zwischen einem Tool und einem Co-Developer.

⸻

## Der echte Vergleich: Opus 4.6 vs. GPT-5.4 vs. Gemini 2.5 Pro

Ich teste neue Modelle, sobald sie rauskommen. Das ist Teil meines Workflows. Hier ist meine ehrliche Einschätzung nach intensiver Nutzung aller drei:

**vs. GPT-5.4**

GPT-5.4 schreibt ordentlichen, sauberen Code. Die Konventionen stimmen, die Tests sind gut strukturiert. Für isolierte Aufgaben – eine Funktion schreiben, einen API-Endpoint bauen – ist GPT-5.4 stark.

Sobald es aber komplex wird, merke ich den Unterschied. Opus 4.6 nutzt den Millionen-Token-Kontext, um die gesamte Codebase zu verstehen und dann präzise zu operieren. Das gibt ihm beim Debugging und Refactoring in grossen Projekten einen klaren Vorteil. GPT-5.4 verliert in komplexen, weitreichenden Aufgaben die Übersicht schneller.

**vs. Gemini 2.5 Pro**

Gemini 2.5 Pro ist schnell – merklich schneller als Opus 4.6. Für schnelles Prototyping, für iteratives Experimentieren, ist das ein realer Vorteil. Wer oft mal schnell etwas ausprobieren will, ist mit Gemini gut bedient.

Für komplexe TypeScript-Projekte und System-Design-Aufgaben ist Opus 4.6 jedoch deutlich stärker. Gemini tendiert dazu, einfachere Lösungen zu bevorzugen, auch wenn die Aufgabe Tiefe erfordert. Opus denkt architektonisch.

**Mein Fazit:**
- Für Production-Code in komplexen Projekten: Opus 4.6, ohne Frage
- Für schnelle Experimente und Prototypen: Gemini 2.5 Pro ist eine valide Option
- Für isolierte, klar umrissene Aufgaben: GPT-5.4 ist stark

Da ich hauptsächlich in grossen, komplexen Projekten arbeite, bleibt Opus 4.6 meine erste Wahl.

⸻

## Mein täglicher Workflow mit Opus 4.6

Hier ist, wie ein typischer Tag bei mir aussieht:

**Morgens:** Tagesplanung mit Claude. Ich beschreibe, was ich erreichen will. Opus 4.6 hilft mir, die Reihenfolge zu strukturieren, Abhängigkeiten zu identifizieren und Stolperfallen frühzeitig zu sehen.

**Tagsüber (Pair Programming):**
• Ich: Business-Logik, Requirements, Product-Entscheidungen
• Claude: Implementation, Optimierung, Debugging
• Gemeinsam: Architektur-Entscheidungen in Echtzeit

**Abends:** Refactoring und Code-Review. Claude liest meinen gesamten Code des Tages und gibt mir Feedback – nicht nur zu Bugs, sondern zu Konsistenz, Lesbarkeit und langfristiger Wartbarkeit.

Was mich täglich begeistert: Das Modell weiss, wann es nachfragen soll, und wann es einfach liefern soll. Diese Balance fehlt vielen Modellen.

⸻

## Ist es seinen Preis wert?

$200 im Monat für Claude Code Max ist kein Kleingeld. Aber ich habe aufgehört, das in Relation zu einem SaaS-Abo zu setzen – das ist die falsche Referenz.

Die richtige Frage ist: Was würde ich zahlen, um täglich einen Senior Developer an meiner Seite zu haben? Die Antwort ist um ein Vielfaches höher.

Als Solo-Developer kann ich mit Opus 4.6 Projekte umsetzen, die früher ein Team erfordert hätten. Das ist kein Hype – das ist meine gelebte Realität seit dem 5. Februar 2026.

⸻

## Fazit: Eine neue Kategorie

Opus 4.6 ist für mich kein weiteres AI-Modell in meinem Roster. Es ist mein primäres Entwicklungs-Werkzeug geworden – so wie IntelliJ meine IDE ist, ist Claude mein Co-Developer.

Die Kombination aus 1M Token Kontext, verlässlichen agentic Sessions und der Fähigkeit, wirklich architektonisch zu denken, hat meine Art zu entwickeln fundamental verändert.

Wenn Sie heute noch nicht mit Opus 4.6 arbeiten: Fangen Sie an. Die Lernkurve ist flach, und der Produktivitätsgewinn kommt schnell.

*Patrik Germann*
*Solo-Developer, AIpuna App*
*Claude-Nutzer seit April 2025*`,
      en: `April 13, 2026 – 10 min read

Claude Opus 4.6 has been live since February 5, 2026 — and I've been using it every single day since. Not because I got swept up in the hype, but because it's simply the best tool I've had for complex software development.

In this post, I'll explain why Opus 4.6 wasn't just an upgrade for me, but a new category entirely — and how it holds up against GPT-5.4 and Gemini 2.5 Pro in real-world daily use.

⸻

## The Numbers, Before We Talk Experience

I rarely start with benchmarks because they often have little to do with actual work. But with Opus 4.6, the numbers are genuinely meaningful:

• **Terminal-Bench 2.0** (agentic coding): 65.4% — the highest score across all frontier models
• **ARC-AGI-2**: 68.8% — compared to 37.6% for Opus 4.5 and 54.2% for GPT-5.2. The largest single-generation leap
• **SWE-bench verified**: 80.8% — real software engineering tasks, solved
• **OSWorld**: 72.7% — autonomous computer use, best in class
• **GDPval-AA**: ~144 Elo points ahead of GPT-5.2 on economically valuable knowledge work

What impresses me most about these numbers: they actually reflect what I experience daily. That's rare.

⸻

## What 1 Million Token Context Actually Means

1 million token context sounds abstract. In practice it means:

I can load my entire project into a single context. Not just individual files, not just modules — the whole thing. Claude then sees all dependencies, all interfaces, all existing patterns simultaneously.

This changes how I work:
• Refactoring across 20+ files in a single session — without losing context
• Bugs that span multiple layers get recognized all at once
• Architecture decisions are made with full knowledge of the entire codebase

Before, I always felt like I was throwing puzzle pieces at the model. Now it sees the complete picture.

⸻

## Agentic Sessions: When Claude Just Gets to Work

What surprised me most about Opus 4.6: its ability to work truly autonomously without losing the thread.

With earlier models, I regularly ran into the problem of long sessions drifting — the model forgetting decisions, repeating itself, or starting to make compromises I didn't want.

Opus 4.6 holds the line. In multi-hour coding sessions, it stays consistent:
- Same coding style, even after 50+ iterations
- Memory of architecture decisions from the beginning of the session
- Actively calling out inconsistencies I introduced myself

That's the difference between a tool and a co-developer.

⸻

## The Real Comparison: Opus 4.6 vs. GPT-5.4 vs. Gemini 2.5 Pro

I test new models as soon as they drop. It's part of my workflow. Here's my honest assessment after intensive use of all three:

**vs. GPT-5.4**

GPT-5.4 writes clean, tidy code. Conventions are solid, tests are well-structured. For isolated tasks — writing a function, building an API endpoint — GPT-5.4 is strong.

But once things get complex, I feel the difference. Opus 4.6 uses its million-token context to understand the entire codebase and then operate with precision. That gives it a clear advantage in debugging and refactoring on large projects. GPT-5.4 loses the overview faster in complex, far-reaching tasks.

**vs. Gemini 2.5 Pro**

Gemini 2.5 Pro is fast — noticeably faster than Opus 4.6. For quick prototyping and iterative experimentation, that's a real advantage. If you want to quickly try things out, Gemini serves you well.

But for complex TypeScript projects and system design tasks, Opus 4.6 is significantly stronger. Gemini tends to prefer simpler solutions even when the task requires depth. Opus thinks architecturally.

**My verdict:**
- For production code in complex projects: Opus 4.6, without question
- For quick experiments and prototypes: Gemini 2.5 Pro is a valid option
- For isolated, clearly defined tasks: GPT-5.4 is strong

Since I mainly work on large, complex projects, Opus 4.6 remains my first choice.

⸻

## My Daily Workflow with Opus 4.6

Here's what a typical day looks like for me:

**Morning:** Daily planning with Claude. I describe what I want to achieve. Opus 4.6 helps me structure the order, identify dependencies, and spot potential issues early.

**During the day (Pair Programming):**
• Me: Business logic, requirements, product decisions
• Claude: Implementation, optimization, debugging
• Together: Architecture decisions in real time

**Evening:** Refactoring and code review. Claude reads all the code I wrote that day and gives me feedback — not just on bugs, but on consistency, readability, and long-term maintainability.

What impresses me daily: the model knows when to ask and when to just deliver. That balance is missing in many models.

⸻

## Is It Worth the Price?

$200 a month for Claude Code Max isn't small change. But I've stopped comparing it to a SaaS subscription — that's the wrong reference point.

The right question is: what would I pay to have a senior developer by my side every day? The answer is many times higher.

As a solo developer, I can build projects with Opus 4.6 that would previously have required a team. That's not hype — it's my lived reality since February 5, 2026.

⸻

## Conclusion: A New Category

For me, Opus 4.6 isn't just another AI model in my roster. It has become my primary development tool — just as IntelliJ is my IDE, Claude is my co-developer.

The combination of 1M token context, reliable agentic sessions, and the ability to truly think architecturally has fundamentally changed how I develop software.

If you're not working with Opus 4.6 yet: get started. The learning curve is gentle, and the productivity gains come quickly.

*Patrik Germann*
*Solo Developer, AIpuna App*
*Claude user since April 2025*`
    },
    date: '2026-04-13',
    category: 'AI',
    readTime: { de: '10 Min. Lesezeit', en: '10 min read' }
  },

  'claude-mythos-das-ki-modell-das-nicht-released-wird': {
    id: 23,
    slug: 'claude-mythos-das-ki-modell-das-nicht-released-wird',
    title: {
      de: 'Claude Mythos: Das KI-Modell, das Anthropic nicht released – und warum das die richtige Entscheidung ist',
      en: 'Claude Mythos: The AI Model Anthropic Won\'t Release — And Why That\'s the Right Decision'
    },
    excerpt: {
      de: 'Anthropic hat ein Modell entwickelt, das so aussergewöhnlich leistungsfähig ist, dass es öffentlich nicht zugänglich gemacht wird. Claude Mythos Preview existiert – aber nur für ein ausgewähltes Konsortium. Was steckt dahinter?',
      en: 'Anthropic has developed a model so extraordinarily capable that it\'s not being made publicly available. Claude Mythos Preview exists — but only for a select consortium. What\'s behind it?'
    },
    content: {
      de: `13. April 2026 – 9 Min. Lesezeit

Es gibt Momente, in denen man erkennt, dass wir uns in einem echten Wendepunkt befinden. Die Geschichte von Claude Mythos ist so ein Moment.

Anthropic hat ein Modell entwickelt – und dann entschieden, es nicht zu veröffentlichen. Nicht weil es nicht funktioniert. Sondern weil es zu gut ist.

Das ist neu. Und es lohnt sich, genauer hinzusehen.

⸻

## Wie Mythos ans Licht kam

Es begann nicht mit einer offiziellen Ankündigung, sondern mit einem Versehen. Anfang 2026 wurden durch eine Sicherheitslücke bei Anthropic intern Informationen zu einem neuen Modell öffentlich – einem Modell mit Fähigkeiten, die damals niemand auf dem Radar hatte.

Anthropic reagierte schnell und bestätigte die Existenz von Claude Mythos Preview. Im April 2026 folgte die offizielle Kommunikation – nicht in Form eines klassischen Produkt-Launches, sondern als Erklärung: Warum dieses Modell nicht öffentlich zugänglich gemacht wird, und was stattdessen damit geplant ist.

Diese Transparenz ist bemerkenswert. Andere Unternehmen hätten das Ganze unter den Tisch fallen lassen. Anthropic hat es zur Grundsatzdiskussion gemacht.

⸻

## Was Mythos kann – und was das bedeutet

Die Fähigkeiten von Claude Mythos Preview sind in einem Bereich besonders aussergewöhnlich: Cybersecurity.

Das klingt vielleicht technisch und abstrakt. Es ist es nicht. Hier sind konkrete Fakten:

• Mythos hat Tausende kritische Sicherheitslücken identifiziert – darunter Zero-Day-Schwachstellen in allen grossen Betriebssystemen und Webbrowsern
• Es hat einen 27 Jahre alten Bug in OpenBSD entdeckt – eine Lücke, die über fast drei Jahrzehnte unentdeckt geblieben war
• Bei expert-level CTF-Aufgaben (Capture the Flag) – Sicherheitschallenges, die bisher kein KI-Modell auch nur ansatzweise bewältigen konnte – erreicht Mythos eine Erfolgsrate von 73%
• Es war das erste Modell, das "The Last Ones" vollständig gelöst hat – eine 32-Schritte-Simulation eines Unternehmens-Netzwerkangriffs
• Und: Es hat echte, bisher unbekannte Zero-Day-Schwachstellen in Firefox gefunden – nicht simulierte, sondern in produktiver Software

Auf SWE-bench verified erreicht Mythos 93.9% – zum Vergleich: Opus 4.6, das beste öffentlich verfügbare Modell, erreicht 80.8%.

Das ist kein schrittweiser Fortschritt. Das ist ein Qualitätssprung.

⸻

## Warum es nicht öffentlich gemacht wird

Die Entscheidung, Mythos nicht zu veröffentlichen, ist keine Markt-Strategie. Es ist eine Sicherheitsentscheidung.

Ein Modell, das Zero-Days in produktiver Software findet, das Unternehmens-Netzwerke simuliert angreifen kann, und das Exploits für bekannte CVEs autonom entwickeln kann – dieses Modell in die Hände der Allgemeinheit zu geben wäre unverantwortlich.

Das ist Anthropics Schlussfolgerung. Und ich finde sie richtig.

Frontier-KI-Entwicklung steht immer vor dem Dilemma zwischen Offenheit und Verantwortung. Mythos ist ein Fall, in dem die Balance klar auf der Seite der Verantwortung liegt. Die Fähigkeit, reale Schwachstellen in weitverbreiteter Software zu finden, hat zu grosses Schadenspotenzial, wenn sie unkontrolliert zugänglich ist.

⸻

## Project Glasswing: Verantwortungsvoller Einsatz

Anstelle eines öffentlichen Releases hat Anthropic Project Glasswing ins Leben gerufen: ein Konsortium aus führenden Technologieunternehmen, die gemeinsam mit Mythos Preview arbeiten – mit dem expliziten Ziel, Sicherheitslücken in kritischer Software zu identifizieren und zu schliessen, bevor sie ausgenutzt werden können.

Das Konsortium umfasst:
• AWS, Apple, Cisco, CrowdStrike
• Google, JPMorgan Chase, Linux Foundation
• Microsoft, NVIDIA, Palo Alto Networks

Anthropic stellt dafür 100 Millionen US-Dollar in Usage Credits bereit.

Das ist ein echter Paradigmenwechsel. Statt "release and see what happens" wird Frontier-KI als Infrastruktur für proaktive Sicherheitsforschung eingesetzt. Die Unternehmen, die am meisten durch Sicherheitslücken gefährdet sind, bekommen Zugang zum mächtigsten Tool, um diese Lücken zu finden – bevor Angreifer es tun.

⸻

## Was das für die Zukunft der KI-Entwicklung bedeutet

Mythos ist eine Zäsur – nicht nur wegen seiner Fähigkeiten, sondern wegen der Entscheidung, die damit verbunden ist.

Es zeigt, dass Anthropic bereit ist, ein kommerziell wertvolles Produkt nicht zu veröffentlichen, wenn die Risiken zu hoch sind. Das ist eine Aussage über Werte, nicht nur über Produkt-Strategie.

Für die KI-Branche als Ganzes stellt sich jetzt die Frage: Wie gehen andere Unternehmen mit ähnlichen Fähigkeiten um? Ist das der Beginn eines neuen Standards – Frontier-Modelle zunächst in kontrollierten Umgebungen einzusetzen, bevor sie breiter zugänglich gemacht werden?

Ich hoffe es.

⸻

## Fazit: Manchmal ist Zurückhaltung Stärke

Claude Mythos Preview ist technisch das beeindruckendste KI-Modell, über das ich je geschrieben habe. Und gleichzeitig ist die Entscheidung, es nicht zu veröffentlichen, genau so beeindruckend.

Es braucht Mut, eine Technologie zurückzuhalten – besonders in einem Markt, in dem Geschwindigkeit und Ankündigungen zählen. Anthropic hat mit Mythos und Project Glasswing gezeigt, dass verantwortungsvolle KI-Entwicklung keine leeren Worte sind, sondern gelebte Praxis.

Das ist der Stand der Dinge im April 2026. Und ich bin gespannt, was Project Glasswing in den kommenden Monaten entdecken wird.

*Patrik Germann*`,
      en: `April 13, 2026 – 9 min read

There are moments when you realize we're at a genuine turning point. The story of Claude Mythos is one of those moments.

Anthropic has developed a model — and then decided not to release it. Not because it doesn't work. Because it works too well.

That's new. And it's worth looking more closely.

⸻

## How Mythos Came to Light

It didn't begin with an official announcement, but with a mistake. In early 2026, a security oversight at Anthropic allowed internal information about a new model to become public — a model with capabilities that no one had on their radar at the time.

Anthropic responded quickly and confirmed the existence of Claude Mythos Preview. The official communication followed in April 2026 — not in the form of a classic product launch, but as an explanation: why this model is not being made publicly available, and what is planned for it instead.

This transparency is remarkable. Other companies would have buried the whole thing. Anthropic made it a matter of principle.

⸻

## What Mythos Can Do — And What That Means

The capabilities of Claude Mythos Preview are particularly extraordinary in one area: cybersecurity.

That might sound technical and abstract. It's not. Here are concrete facts:

• Mythos identified thousands of critical security vulnerabilities — including zero-day flaws in all major operating systems and web browsers
• It discovered a 27-year-old bug in OpenBSD — a vulnerability that had gone undetected for nearly three decades
• On expert-level CTF (Capture the Flag) tasks — security challenges that no previous AI model had been able to solve — Mythos achieves a 73% success rate
• It was the first model to fully solve "The Last Ones" — a 32-step simulation of a corporate network attack
• And: it found real, previously unknown zero-day vulnerabilities in Firefox — not simulated, but in production software

On SWE-bench verified, Mythos reaches 93.9% — for comparison: Opus 4.6, the best publicly available model, achieves 80.8%.

This isn't incremental progress. It's a quality leap.

⸻

## Why It's Not Being Released Publicly

The decision not to release Mythos is not a market strategy. It's a safety decision.

A model that finds zero-days in production software, that can simulate attacking corporate networks, and that can autonomously develop exploits for known CVEs — giving this to the general public would be irresponsible.

That's Anthropic's conclusion. And I think they're right.

Frontier AI development always faces the dilemma between openness and responsibility. Mythos is a case where the balance clearly lies on the side of responsibility. The ability to find real vulnerabilities in widely-used software has too much potential for harm if made available without controls.

⸻

## Project Glasswing: Responsible Deployment

Instead of a public release, Anthropic has launched Project Glasswing: a consortium of leading technology companies that work together with Mythos Preview — with the explicit goal of identifying and closing security vulnerabilities in critical software before they can be exploited.

The consortium includes:
• AWS, Apple, Cisco, CrowdStrike
• Google, JPMorgan Chase, Linux Foundation
• Microsoft, NVIDIA, Palo Alto Networks

Anthropic is committing $100 million in usage credits to the effort.

This is a genuine paradigm shift. Instead of "release and see what happens," frontier AI is being deployed as infrastructure for proactive security research. The companies most at risk from security vulnerabilities get access to the most powerful tool for finding those vulnerabilities — before attackers do.

⸻

## What This Means for the Future of AI Development

Mythos is a watershed moment — not just because of its capabilities, but because of the decision that came with it.

It shows that Anthropic is willing to not release a commercially valuable product when the risks are too high. That's a statement about values, not just about product strategy.

For the AI industry as a whole, the question now is: how do other companies handle similar capabilities? Is this the beginning of a new standard — deploying frontier models in controlled environments before making them more broadly available?

I hope so.

⸻

## Conclusion: Sometimes Restraint Is Strength

Claude Mythos Preview is technically the most impressive AI model I've ever written about. And at the same time, the decision not to release it is equally impressive.

It takes courage to hold back a technology — especially in a market where speed and announcements matter. With Mythos and Project Glasswing, Anthropic has shown that responsible AI development isn't empty words, but lived practice.

That's the state of things in April 2026. And I'm curious what Project Glasswing will discover in the months to come.

*Patrik Germann*`
    },
    date: '2026-04-13',
    category: 'AI',
    readTime: { de: '9 Min. Lesezeit', en: '9 min read' }
  },

  'ein-monat-openai-codex-dann-hat-claude-aufgeraeumt': {
    id: 24,
    slug: 'ein-monat-openai-codex-dann-hat-claude-aufgeraeumt',
    title: {
      de: 'Ein Monat OpenAI Codex 5.2 – und wie Opus 4.6 danach aufgeräumt hat',
      en: 'One Month with OpenAI Codex 5.2 — And How Opus 4.6 Cleaned It Up Afterward'
    },
    excerpt: {
      de: 'Ich habe Codex 5.2 eine echte Chance gegeben. Es hat geliefert – aber zurückgelassen hat es technische Schulden. Was ich danach mit Claude Opus 4.6 gelernt habe, ist wertvoller als das Experiment selbst.',
      en: 'I gave Codex 5.2 a real shot. It delivered — but what it left behind was technical debt. What I learned with Claude Opus 4.6 afterward is more valuable than the experiment itself.'
    },
    content: {
      de: `13. April 2026 – 11 Min. Lesezeit

Ich bin jemand, der neue Tools ernsthaft ausprobiert. Nicht für einen Nachmittag, sondern wirklich – mit echten Projekten, echtem Code, echtem Druck.

Als OpenAI im Dezember 2025 Codex 5.2 veröffentlichte, war klar: Das ist einen richtigen Test wert. Also habe ich es gemacht.

Was dabei herauskam, war interessanter als ich erwartet hatte – und die grösste Erkenntnis kam nicht aus dem Experiment selbst, sondern aus dem, was danach passierte.

⸻

## Codex 5.2: Was es gut macht

Ich beginne dort, wo Codex 5.2 wirklich glänzt – denn das tut es.

**Liefergeschwindigkeit.** Wenn ich ein klares Feature beschreibe, liefert Codex schnell. Nicht immer perfekt, aber schnell. Für einen ersten Entwurf, für einen Proof of Concept, für das Ausformulieren einer Idee in Code – da ist Codex agil und direkt.

**Grosse Repositories.** Die Arbeit in grossen Codebasen, das Navigieren über Modulgrenzen hinweg – da hat Codex tatsächlich Stärken. Context Compaction funktioniert gut für lange Sessions in grossen Projekten, und die Resultate sind solide.

**Refactoring und Migrationen.** Für grössere Code-Änderungen, Umstrukturierungen, das Portieren von altem Code – da zeigt Codex, was es kann. Besonders bei grossen, klar definierten Umbauten liefert es zuverlässig.

**Visuelle Verarbeitung.** Screenshots, UI-Diagramme, technische Zeichnungen – Codex versteht visuelle Inputs gut und kann daraus funktionierenden Code generieren. Das hat mir beim Umsetzen von Design-Mockups wirklich Zeit gespart.

Das ist nicht wenig. Und in den ersten Wochen war ich begeistert von der Geschwindigkeit.

⸻

## Was sich im Laufe der Zeit zeigte

Dann kam, was immer kommt, wenn man wirklich tief mit einem Werkzeug arbeitet: Die Muster werden sichtbar.

**Komplexität als Standard-Lösung.** Codex neigt dazu, komplexere Lösungen zu bauen als notwendig. Nicht falsch, nicht kaputt – aber aufwändiger als es sein müsste. Abstraktionsschichten, die niemand angefragt hat. Hilfsmethoden, die genau einmal verwendet werden. Konfigurationsoptionen ohne konkrete Aufgabe.

**Abhängigkeiten, die sich summieren.** Über Wochen hinweg sammelten sich Abhängigkeiten an, die teils unnötig waren, teils miteinander in Spannung standen. Einzeln betrachtet harmlos, zusammen ein wachsendes Problem.

**Absicht und Beschreibung.** Codex ist gut darin, technisch korrekte Lösungen zu bauen. Manchmal hat es meine Absicht aber nicht ganz getroffen – der Code tat das Beschriebene, nicht das Gemeinte. Das ist ein feiner, aber wichtiger Unterschied, der sich mit der Zeit akkumuliert.

**Lange autonome Sessions.** Bei wirklich langen, autonomen Sessions hatte Codex manchmal Mühe, den Faden zu halten. Es hörte auf, fragte nach, oder lieferte unvollständige Ergebnisse, die ich dann fertigstellen musste.

Nichts davon ist katastrophal. Der Code lief. Aber er hatte mir etwas zu zahlen.

⸻

## Opus 4.6 kommt ins Spiel

Nach etwa einem Monat mit Codex 5.2 – nicht einem zusammenhängenden Monat, sondern über Wochen verteilt, immer wieder – war klar, dass es Zeit für eine andere Perspektive war.

Ich habe angefangen, einzelne Module mit Opus 4.6 zu reviewen. Nicht mit dem Ziel, alles niederzureissen, sondern um zu verstehen, was da ist – und was besser sein könnte.

Was dann folgte, war ein schrittweiser, iterativer Prozess:

1. **Audit:** Opus 4.6 analysierte den bestehenden Code, identifizierte Muster, kartierte Abhängigkeiten
2. **Priorisierung:** Gemeinsam entschieden wir, was wirklich angefasst werden muss
3. **Refactoring in Wellen:** Modul für Modul, mit klarem Fokus auf Vereinfachung
4. **Abhängigkeiten bereinigen:** Unnötige Packages raus, Konflikte aufgelöst
5. **Tests nachziehen:** Wo Tests fehlten, wurden sie ergänzt

Das war keine schnelle Sache. Es war Arbeit über mehrere Wochen – aber es hat sich gelohnt.

⸻

## Was der Prozess mir gezeigt hat

Hier ist das Interessante: Ich glaube nicht, dass das Codex-Experiment ein Fehler war.

Der Code, den Codex produziert hat, hat funktioniert. Das Projekt hat sich vorwärtsbewegt. Und der Kontakt mit einem anderen Modell hat mir Dinge über meinen eigenen Stil gezeigt, die ich so nicht gesehen hätte.

Die eigentliche Erkenntnis kam beim Aufräumen mit Opus 4.6.

**Code-Qualität ist keine unmittelbare Eigenschaft.** Sie zeigt sich über Zeit. Ein Feature, das heute funktioniert, kann in drei Monaten zur Quelle von Problemen werden – nicht weil es kaputt ist, sondern weil es zu komplex ist, zu eng gekoppelt, zu schwer zu ändern.

**Opus 4.6 denkt in Systemen.** Was mir beim Cleanup aufgefallen ist: Opus sieht nicht nur den Code vor sich, sondern das System, das er beschreibt. Es fragt nicht nur "Funktioniert das?", sondern "Ist das das Richtige?"

**Die Fähigkeit aufzuräumen ist genauso wichtig wie die Fähigkeit zu bauen.** Das klingt trivial, ist es aber nicht. Nicht jedes Tool – nicht jeder Entwickler – ist gut in beidem. Zu wissen, welches Werkzeug man wann braucht, ist die eigentliche Kompetenz.

⸻

## Was ich anders machen würde

Wenn ich das Experiment heute noch einmal starten würde, würde ich es mit klaren Checkpoints starten.

Nicht "teste Codex 5.2 für einen Monat" – sondern "teste Codex 5.2 für konkrete Aufgaben, und überprüfe wöchentlich die Code-Qualität."

Der Unterschied: Ich hätte früher gesehen, wo sich technische Schulden aufbauen – und hätte sie kontinuierlich adressiert, statt am Ende aufzuräumen.

Das ist übrigens eine Lektion, die unabhängig von jedem AI-Tool gilt.

⸻

## Verschiedene Tools, verschiedene Stärken

Was bleibt nach diesem Experiment?

Codex 5.2 ist ein ernstzunehmendes Werkzeug. Für schnelle Lieferung, für grosse Refactorings, für Prototypen – es hat seinen Platz. Wer weiss, wann er es einsetzen soll, kann damit produktiv sein.

Opus 4.6 ist mein Werkzeug für alles, was über die nächsten Wochen und Monate hinaus leben muss. Wo Code nicht nur funktionieren, sondern auch lesbar, wartbar und erweiterbar sein muss.

Das sind nicht nur zwei verschiedene Modelle. Das sind zwei verschiedene Philosophien beim Code-Schreiben – und zu wissen, wann man welche braucht, ist die eigentliche Kompetenz.

⸻

## Fazit: Das Experiment hat sich gelohnt

Ich würde das Experiment wieder machen. Weil ich mit jedem Test lerne – über die Tools, über meinen eigenen Stil, und über das, was ich wirklich von einem Coding-Partner erwarte.

Und was ich jetzt weiss: Für meine Art zu arbeiten, für Projekte mit langem Atem, für Code, der in einem Jahr noch gut aussehen soll – da ist Claude Opus 4.6 das Richtige.

Codex 5.2 hat mir das auf seine Weise bestätigt.

*Patrik Germann*
*Solo-Developer, AIpuna App*`,
      en: `April 13, 2026 – 11 min read

I'm someone who tests new tools seriously. Not for an afternoon, but genuinely — with real projects, real code, real pressure.

When OpenAI released Codex 5.2 in December 2025, it was clear: this deserves a proper test. So I did it.

What came out of it was more interesting than I expected — and the biggest insight came not from the experiment itself, but from what happened afterward.

⸻

## Codex 5.2: Where It Excels

I'll start where Codex 5.2 genuinely shines — because it does.

**Delivery speed.** When I describe a clear feature, Codex delivers fast. Not always perfect, but fast. For a first draft, for a proof of concept, for turning an idea into code — Codex is agile and direct.

**Large repositories.** Working in large codebases, navigating across module boundaries — Codex has real strengths there. Context compaction works well for long sessions in large projects, and the results are solid.

**Refactoring and migrations.** For larger code changes, restructuring, porting old code — Codex shows what it can do. Especially for large, clearly defined restructurings, it delivers reliably.

**Visual processing.** Screenshots, UI diagrams, technical drawings — Codex handles visual inputs well and can generate working code from them. That genuinely saved me time when translating design mockups into code.

That's not nothing. And in the first weeks, I was impressed by the speed.

⸻

## What Showed Up Over Time

Then came what always comes when you work deeply with a tool: the patterns become visible.

**Complexity as the default solution.** Codex tends to build more complex solutions than necessary. Not wrong, not broken — but more elaborate than it needs to be. Abstraction layers nobody asked for. Helper methods used exactly once. Configuration options with no job to do.

**Dependencies that add up.** Over weeks, dependencies accumulated — some unnecessary, some in tension with each other. Harmless individually, a growing problem together.

**Intent and description.** Codex is good at building technically correct solutions. But sometimes it didn't quite catch my intent — the code did what I described, not what I meant. That's a subtle but important difference that compounds over time.

**Long autonomous sessions.** In truly long, autonomous sessions, Codex sometimes struggled to maintain the thread. It would stop, ask questions, or deliver incomplete results that I then had to finish.

None of this is catastrophic. The code ran. But it owed me something.

⸻

## Opus 4.6 Enters the Picture

After about a month with Codex 5.2 — not a continuous month, but spread out over weeks, repeatedly — it was clear that a different perspective was needed.

I started reviewing individual modules with Opus 4.6. Not with the goal of tearing everything down, but of understanding what was there — and what could be better.

What followed was a gradual, iterative process:

1. **Audit:** Opus 4.6 analyzed the existing code, identified patterns, mapped dependencies
2. **Prioritization:** We decided together what actually needed to be touched
3. **Refactoring in waves:** Module by module, with clear focus on simplification
4. **Dependency cleanup:** Unnecessary packages removed, conflicts resolved
5. **Tests added:** Where tests were missing, they were written

This wasn't quick. It was work over several weeks — but it was worth it.

⸻

## What the Process Revealed

Here's the interesting part: I don't think the Codex experiment was a mistake.

The code Codex produced worked. The project moved forward. And contact with a different model showed me things about my own style I wouldn't have seen otherwise.

The real insight came during the cleanup with Opus 4.6.

**Code quality is not an immediate property.** It shows itself over time. A feature that works today can become the source of problems three months from now — not because it's broken, but because it's too complex, too tightly coupled, too hard to change.

**Opus 4.6 thinks in systems.** What stood out during cleanup: Opus doesn't just see the code in front of it, but the system it describes. It doesn't just ask "Does this work?" but "Is this the right thing?"

**The ability to clean up is just as important as the ability to build.** That sounds trivial, but it isn't. Not every tool — not every developer — is good at both. Knowing which tool you need and when is the actual skill.

⸻

## What I Would Do Differently

If I were starting the experiment today, I'd start it with clear checkpoints.

Not "try Codex 5.2 for a month" — but "try Codex 5.2 for specific tasks, and review code quality weekly."

The difference: I would have seen earlier where technical debt was building up — and would have addressed it continuously, rather than cleaning it up at the end.

That's a lesson that applies regardless of which AI tool you're using.

⸻

## Different Tools, Different Strengths

What remains after this experiment?

Codex 5.2 is a tool worth taking seriously. For fast delivery, for large refactors, for prototypes — it has its place. Those who know when to deploy it can be productive with it.

Opus 4.6 is my tool for everything that needs to live beyond the next few weeks and months. Where code doesn't just need to work, but also needs to be readable, maintainable, and extensible.

Those aren't just two different models. They're two different philosophies of writing code — and knowing when you need which is the actual skill.

⸻

## Conclusion: The Experiment Was Worth It

I would run the experiment again. Because with every test I learn — about the tools, about my own style, and about what I really expect from a coding partner.

And what I know now: for my way of working, for projects with long-term ambition, for code that should still look good a year from now — Claude Opus 4.6 is the right choice.

Codex 5.2 confirmed that for me, in its own way.

*Patrik Germann*
*Solo Developer, AIpuna App*`
    },
    date: '2026-04-13',
    category: 'Development',
    readTime: { de: '11 Min. Lesezeit', en: '11 min read' }
  }
}
